import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

/**
 * Tutorial steps — 7 quick steps covering the main areas.
 */
export const TUTORIAL_STEPS = [
  {
    id: 'reservaties-main',
    targetSelector: '[data-tutorial-target="reservaties-main"]',
    titleKey: 'reservations_title',
    descriptionKey: 'reservations_desc',
    position: 'right',
  },
  {
    id: 'cadeaubonnen-main',
    targetSelector: '[data-tutorial-target="cadeaubonnen-main"]',
    titleKey: 'giftcards_title',
    descriptionKey: 'giftcards_desc',
    position: 'right',
  },
  {
    id: 'contacts-main',
    targetSelector: '[data-tutorial-target="contacts-main"]',
    titleKey: 'contacts_title',
    descriptionKey: 'contacts_desc',
    position: 'right',
  },
  {
    id: 'email-main',
    targetSelector: '[data-tutorial-target="email-main"]',
    titleKey: 'email_title',
    descriptionKey: 'email_desc',
    position: 'right',
  },
  {
    id: '/settings',
    targetSelector: '[data-tutorial-target="/settings"]',
    titleKey: 'settings_title',
    descriptionKey: 'settings_desc',
    position: 'right',
  },
  {
    id: 'new-reservation-button',
    targetSelector: '[data-tutorial-target="new-reservation-button"]',
    titleKey: 'new_reservation_title',
    descriptionKey: 'new_reservation_desc',
    position: 'left',
  },
  {
    id: 'blocked-slots-button',
    targetSelector: '.blocked-slots-manage-button',
    titleKey: 'blocked_slots_title',
    descriptionKey: 'blocked_slots_desc',
    position: 'left',
  },
];

const TUTORIAL_STORAGE_KEY = 'happychef-dashboard-tutorial-completed';
const TUTORIAL_COMPLETED_AT_KEY = 'happychef-dashboard-tutorial-completed-at';

const TutorialContext = createContext(undefined);

/**
 * TutorialProvider
 * @param {Object} props
 * @param {Object} props.account - Account data from useGetAccountQuery
 * @param {Function} props.updateAccount - Mutation from useUpdateAccountMutation
 * @param {React.ReactNode} props.children
 */
export function TutorialProvider({ account, updateAccount, children }) {
  const [showWelcome, setShowWelcome] = useState(false);
  const [isTutorialActive, setIsTutorialActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [tutorialCompleted, setTutorialCompleted] = useState(true); // default true to prevent flash
  const [tutorialCompletedAt, setTutorialCompletedAt] = useState(null);

  // Determine tutorial completion from account data (DB) with localStorage as fast cache
  useEffect(() => {
    // Hide tutorial entirely for admin visits (_admin login suffix)
    if (localStorage.getItem('adminVisit') === 'true') {
      setTutorialCompleted(true);
      return;
    }

    // Fast check: localStorage
    const localCompleted = localStorage.getItem(TUTORIAL_STORAGE_KEY) === 'true';
    const localCompletedAt = localStorage.getItem(TUTORIAL_COMPLETED_AT_KEY);

    if (account) {
      const dbCompleted = !!account.tutorialCompleted;
      const dbCompletedAt = account.tutorialCompletedAt || null;

      // Sync: if DB says completed, update localStorage
      if (dbCompleted) {
        localStorage.setItem(TUTORIAL_STORAGE_KEY, 'true');
        if (dbCompletedAt) {
          localStorage.setItem(TUTORIAL_COMPLETED_AT_KEY, dbCompletedAt);
        }
      }

      const isCompleted = dbCompleted || localCompleted;
      setTutorialCompleted(isCompleted);
      setTutorialCompletedAt(dbCompletedAt || localCompletedAt || null);

      if (!isCompleted) {
        const timer = setTimeout(() => setShowWelcome(true), 1500);
        return () => clearTimeout(timer);
      }
    } else if (!localCompleted) {
      // Account not loaded yet but localStorage says not completed — wait for account
      setTutorialCompleted(false);
    }
  }, [account]);

  const markCompleted = useCallback(async () => {
    const now = new Date().toISOString();
    localStorage.setItem(TUTORIAL_STORAGE_KEY, 'true');
    localStorage.setItem(TUTORIAL_COMPLETED_AT_KEY, now);
    setTutorialCompleted(true);
    setTutorialCompletedAt(now);

    // Persist to DB
    if (updateAccount) {
      try {
        await updateAccount({ tutorialCompleted: true, tutorialCompletedAt: now }).unwrap();
      } catch (err) {
        console.error('[Tutorial] Failed to save completion to account:', err);
      }
    }
  }, [updateAccount]);

  const startTutorial = useCallback(() => {
    setShowWelcome(false);
    setCurrentStep(0);
    setIsTutorialActive(true);
  }, []);

  const skipTutorial = useCallback(() => {
    setShowWelcome(false);
    setIsTutorialActive(false);
    markCompleted();
    window.dispatchEvent(new CustomEvent('tutorial:end'));
  }, [markCompleted]);

  const finishTutorial = useCallback(() => {
    setIsTutorialActive(false);
    setCurrentStep(0);
    markCompleted();
    window.dispatchEvent(new CustomEvent('tutorial:end'));
  }, [markCompleted]);

  const nextStep = useCallback(() => {
    if (currentStep < TUTORIAL_STEPS.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      finishTutorial();
    }
  }, [currentStep, finishTutorial]);

  const prevStep = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  }, [currentStep]);

  const restartTutorial = useCallback(async () => {
    localStorage.removeItem(TUTORIAL_STORAGE_KEY);
    localStorage.removeItem(TUTORIAL_COMPLETED_AT_KEY);
    setTutorialCompleted(false);
    setTutorialCompletedAt(null);
    setCurrentStep(0);
    setShowWelcome(true);
    setIsTutorialActive(false);

    if (updateAccount) {
      try {
        await updateAccount({ tutorialCompleted: false, tutorialCompletedAt: null }).unwrap();
      } catch (err) {
        console.error('[Tutorial] Failed to reset completion in account:', err);
      }
    }
  }, [updateAccount]);

  const currentStepData = isTutorialActive ? TUTORIAL_STEPS[currentStep] : null;

  return (
    <TutorialContext.Provider
      value={{
        showWelcome,
        isTutorialActive,
        tutorialCompleted,
        tutorialCompletedAt,
        currentStep,
        totalSteps: TUTORIAL_STEPS.length,
        currentStepData,
        startTutorial,
        skipTutorial,
        nextStep,
        prevStep,
        finishTutorial,
        restartTutorial,
      }}
    >
      {children}
    </TutorialContext.Provider>
  );
}

export function useTutorial() {
  const context = useContext(TutorialContext);
  if (context === undefined) {
    throw new Error('useTutorial must be used within a TutorialProvider');
  }
  return context;
}
